import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import { FileDropzone } from "components/common/FileDropzone";
import {
  approveWorkOrderForBilling,
  cancelWorkOrder,
  closeWorkOrder,
  holdWorkOrder,
  rejectWorkOrderCompletion,
  resumeWorkOrder,
  startWorkOrder,
  submitWorkOrderBilling,
  submitWorkOrderCompletion,
} from "features/WorkOrders/services/WorkOrderService";
import type { WorkOrder } from "features/WorkOrders/types/work-order.types";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import {
  ClientAdvanceSourceType,
  ClientAdvanceStatus,
} from "features/ClientAdvances/constants/client-advance.constants";
import type { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";

export type WorkOrderLifecycleIntent =
  | { kind: "start" }
  | { kind: "hold" }
  | { kind: "resume" }
  | { kind: "close" }
  | { kind: "submitCompletion" }
  | { kind: "approveForBilling" }
  | { kind: "rejectCompletion" }
  | { kind: "submitBilling" }
  | { kind: "cancel" };

type ConfirmKind = "start" | "resume" | "close" | "cancel" | "approveForBilling";

const confirmCopy: Record<
  ConfirmKind,
  { title: string; iconClass: string; iconColor: string; heading: string; description: string; confirm: string; confirmColor: string }
> = {
  start: {
    title: "Start work order",
    iconClass: "ri-play-circle-line",
    iconColor: "text-success",
    heading: "Start work on this order?",
    description:
      "Status will move to In progress. The server may record the actual start time if it was not set already.",
    confirm: "Start work",
    confirmColor: "success",
  },
  resume: {
    title: "Resume work",
    iconClass: "ri-play-circle-line",
    iconColor: "text-success",
    heading: "Resume this work order?",
    description:
      "Work will move back to In progress. The server may set the actual start time if it was never set.",
    confirm: "Resume",
    confirmColor: "success",
  },
  close: {
    title: "Close work order",
    iconClass: "ri-lock-line",
    iconColor: "text-dark",
    heading: "Close this work order?",
    description:
      "Marks the job administratively closed after completion. This action is controlled by the server transition rules.",
    confirm: "Close",
    confirmColor: "dark",
  },
  cancel: {
    title: "Cancel work order",
    iconClass: "ri-close-circle-line",
    iconColor: "text-danger",
    heading: "Cancel this work order?",
    description:
      "This will mark the work order as cancelled. This action cannot be undone. " +
      "Any linked advance that hasn't been received yet is cancelled automatically; " +
      "if one has already been received, this will be blocked until it's refunded first.",
    confirm: "Cancel work order",
    confirmColor: "danger",
  },
  approveForBilling: {
    title: "Approve for billing",
    iconClass: "ri-check-double-line",
    iconColor: "text-success",
    heading: "Approve this completion for billing?",
    description:
      "Status will move to Ready for billing. Accounts can then finalise the billed amount and generate the invoice.",
    confirm: "Approve for billing",
    confirmColor: "success",
  },
};

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

type Props = {
  workOrder: WorkOrder | null;
  intent: WorkOrderLifecycleIntent | null;
  onFinished: () => void;
  onSuccess: () => void | Promise<void>;
};

export default function WorkOrderLifecycleModals({
  workOrder,
  intent,
  onFinished,
  onSuccess,
}: Props) {
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  // Hold
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdSubmitting, setHoldSubmitting] = useState(false);

  // Submit completion
  const [completionOpen, setCompletionOpen] = useState(false);
  const [completionFeedback, setCompletionFeedback] = useState("");
  const [completionFiles, setCompletionFiles] = useState<File[]>([]);
  const [completionAttempted, setCompletionAttempted] = useState(false);
  const [completionSubmitting, setCompletionSubmitting] = useState(false);

  // Reject completion
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  // Submit billing
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingAmount, setBillingAmount] = useState<string>("");
  const [billingSubmitting, setBillingSubmitting] = useState(false);
  const [draftAdvanceCount, setDraftAdvanceCount] = useState(0);

  const runSuccess = useCallback(async () => {
    await onSuccess();
  }, [onSuccess]);

  const closeAll = useCallback(() => {
    setConfirmKind(null);
    setHoldOpen(false);
    setCompletionOpen(false);
    setRejectOpen(false);
    setBillingOpen(false);
  }, []);

  useEffect(() => {
    if (!workOrder || !intent) {
      closeAll();
      return;
    }
    closeAll();
    setHoldReason("");
    setCompletionFeedback("");
    setCompletionFiles([]);
    setCompletionAttempted(false);
    setRejectReason("");
    setBillingAmount(
      workOrder.finalAmount != null ? String(workOrder.finalAmount) : "",
    );
    setDraftAdvanceCount(0);
    switch (intent.kind) {
      case "start":
        setConfirmKind("start");
        break;
      case "hold":
        setHoldOpen(true);
        break;
      case "resume":
        setConfirmKind("resume");
        break;
      case "close":
        setConfirmKind("close");
        break;
      case "cancel":
        setConfirmKind("cancel");
        break;
      case "submitCompletion":
        setCompletionOpen(true);
        break;
      case "approveForBilling":
        setConfirmKind("approveForBilling");
        break;
      case "rejectCompletion":
        setRejectOpen(true);
        break;
      case "submitBilling":
        setBillingOpen(true);
        break;
      default:
        break;
    }
  }, [workOrder, intent, closeAll]);

  // --- Simple confirmations (start, resume, close, cancel, approveForBilling) ---
  const handleConfirmLifecycle = useCallback(async () => {
    if (!workOrder || !confirmKind) return;
    setConfirmSubmitting(true);
    try {
      let res;
      if (confirmKind === "start") res = await startWorkOrder(workOrder.id);
      else if (confirmKind === "resume") res = await resumeWorkOrder(workOrder.id);
      else if (confirmKind === "cancel") res = await cancelWorkOrder(workOrder.id);
      else if (confirmKind === "approveForBilling") res = await approveWorkOrderForBilling(workOrder.id);
      else res = await closeWorkOrder(workOrder.id);

      if (res.success) {
        toast.success(res.message ?? "Updated.");
        setConfirmKind(null);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Request failed.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Request failed."));
    } finally {
      setConfirmSubmitting(false);
    }
  }, [confirmKind, onFinished, runSuccess, workOrder]);

  // --- Hold ---
  const handleHold = useCallback(async () => {
    if (!workOrder || !holdReason.trim()) return;
    setHoldSubmitting(true);
    try {
      const res = await holdWorkOrder(workOrder.id, { reason: holdReason.trim() });
      if (res.success) {
        toast.success(res.message ?? "Work order put on hold.");
        setHoldOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not put work order on hold.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not put work order on hold."));
    } finally {
      setHoldSubmitting(false);
    }
  }, [holdReason, onFinished, runSuccess, workOrder]);

  // --- Submit completion ---
  const handleSubmitCompletion = useCallback(async () => {
    if (!workOrder) return;
    if (!completionFeedback.trim()) {
      toast.error("Please provide completion feedback.");
      return;
    }
    setCompletionAttempted(true);
    if (completionFiles.length === 0) {
      toast.error("Please attach at least one completion document.");
      return;
    }
    setCompletionSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("feedback", completionFeedback.trim());
      for (const file of completionFiles) {
        formData.append("completionDocuments", file);
      }
      const res = await submitWorkOrderCompletion(workOrder.id, formData);
      if (res.success) {
        toast.success(res.message ?? "Completion submitted for approval.");
        setCompletionOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not submit completion.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not submit completion."));
    } finally {
      setCompletionSubmitting(false);
    }
  }, [completionFeedback, completionFiles, onFinished, runSuccess, workOrder]);

  // --- Reject completion ---
  const handleRejectCompletion = useCallback(async () => {
    if (!workOrder) return;
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection.");
      return;
    }
    setRejectSubmitting(true);
    try {
      const res = await rejectWorkOrderCompletion(workOrder.id, { reason: rejectReason.trim() });
      if (res.success) {
        toast.success(res.message ?? "Completion rejected. Work order returned to In progress.");
        setRejectOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not reject completion.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not reject completion."));
    } finally {
      setRejectSubmitting(false);
    }
  }, [onFinished, rejectReason, runSuccess, workOrder]);

  // --- Submit billing ---
  const handleSubmitBilling = useCallback(async () => {
    if (!workOrder) return;
    const parsed = billingAmount.trim() !== "" ? Number(billingAmount) : null;
    if (billingAmount.trim() !== "" && (isNaN(parsed!) || parsed! < 0)) {
      toast.error("Please enter a valid billed amount.");
      return;
    }
    setBillingSubmitting(true);
    try {
      const res = await submitWorkOrderBilling(workOrder.id, {
        finalAmount: parsed,
      });
      if (res.success) {
        toast.success(res.message ?? "Billing submitted. Invoice generated.");
        setBillingOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not submit billing.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not submit billing."));
    } finally {
      setBillingSubmitting(false);
    }
  }, [billingAmount, onFinished, runSuccess, workOrder]);

  const confirmCopyEntry = confirmKind ? confirmCopy[confirmKind] : null;

  return (
    <>
      {/* Simple confirmations */}
      {confirmCopyEntry ? (
        <ConfirmActionModal
          show={Boolean(confirmKind)}
          onClose={() => {
            if (!confirmSubmitting) {
              setConfirmKind(null);
              onFinished();
            }
          }}
          title={confirmCopyEntry.title}
          iconClass={confirmCopyEntry.iconClass}
          iconColor={confirmCopyEntry.iconColor}
          heading={confirmCopyEntry.heading}
          description={confirmCopyEntry.description}
          confirmText={confirmSubmitting ? "Please wait…" : confirmCopyEntry.confirm}
          confirmColor={confirmCopyEntry.confirmColor}
          confirmDisabled={confirmSubmitting}
          onConfirm={() => void handleConfirmLifecycle()}
        />
      ) : null}

      {/* Hold — collects required reason */}
      <Modal
        isOpen={holdOpen}
        toggle={() => { if (!holdSubmitting) { setHoldOpen(false); onFinished(); } }}
        centered
      >
        <ModalHeader toggle={() => { if (!holdSubmitting) { setHoldOpen(false); onFinished(); } }}>
          Put work on hold
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Work will move to <strong>On hold</strong> until someone resumes it.
          </p>
          <Label className="form-label">Reason <span className="text-danger">*</span></Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Explain why the work is being paused…"
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            disabled={holdSubmitting}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={holdSubmitting} onClick={() => { setHoldOpen(false); onFinished(); }}>
            Cancel
          </Button>
          <Button color="secondary" disabled={holdSubmitting || !holdReason.trim()} onClick={() => void handleHold()}>
            {holdSubmitting ? "Please wait…" : "Put on hold"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Submit completion — feedback + documents */}
      <Modal
        isOpen={completionOpen}
        toggle={() => { if (!completionSubmitting) { setCompletionOpen(false); onFinished(); } }}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => { if (!completionSubmitting) { setCompletionOpen(false); onFinished(); } }}>
          Submit completion
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Submitting completion will send this work order for approval before billing.
            Attach all relevant completion documents (photos, sign-off sheets, etc.).
          </p>
          <div className="mb-3">
            <Label className="form-label">
              Completion feedback <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={4}
              placeholder="Describe the work completed, any deviations from scope, materials used, etc."
              value={completionFeedback}
              onChange={(e) => setCompletionFeedback(e.target.value)}
              disabled={completionSubmitting}
            />
          </div>
          <div className="mb-3">
            <Label className="form-label">
              Completion documents <span className="text-danger">*</span>
            </Label>
            <FileDropzone
              accept={{
                "image/*": [".png", ".jpg", ".jpeg", ".webp"],
                "application/pdf": [".pdf"],
                "application/msword": [".doc"],
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                "application/vnd.ms-excel": [".xls"],
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
              }}
              multiple
              compact
              disabled={completionSubmitting}
              invalid={completionAttempted && completionFiles.length === 0}
              description="Images, PDF, Word, Excel"
              onDropAccepted={(files) =>
                setCompletionFiles((prev) => [...prev, ...files])
              }
            />
            {completionFiles.length > 0 && (
              <ul className="list-unstyled mt-2 mb-0 d-flex flex-column gap-1">
                {completionFiles.map((f, i) => (
                  <li key={i} className="d-flex align-items-center gap-2 small">
                    <i className="ri-file-line text-muted flex-shrink-0" />
                    <span className="text-truncate flex-grow-1">{f.name}</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-danger flex-shrink-0"
                      onClick={() =>
                        setCompletionFiles((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      aria-label="Remove file"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={completionSubmitting}
            onClick={() => { setCompletionOpen(false); onFinished(); }}
          >
            Cancel
          </Button>
          <Button
            color="success"
            disabled={completionSubmitting}
            onClick={() => void handleSubmitCompletion()}
          >
            {completionSubmitting ? "Submitting…" : "Submit completion"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject completion — required reason */}
      <Modal
        isOpen={rejectOpen}
        toggle={() => { if (!rejectSubmitting) { setRejectOpen(false); onFinished(); } }}
        centered
      >
        <ModalHeader toggle={() => { if (!rejectSubmitting) { setRejectOpen(false); onFinished(); } }}>
          Reject completion
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            The work order will be returned to <strong>In progress</strong>.
            The assignee will need to resubmit once the issue is resolved.
          </p>
          <Label className="form-label">
            Reason for rejection <span className="text-danger">*</span>
          </Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Explain what needs to be corrected before resubmission…"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={rejectSubmitting}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={rejectSubmitting} onClick={() => { setRejectOpen(false); onFinished(); }}>
            Cancel
          </Button>
          <Button color="danger" disabled={rejectSubmitting || !rejectReason.trim()} onClick={() => void handleRejectCompletion()}>
            {rejectSubmitting ? "Please wait…" : "Reject completion"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Submit billing — optional finalAmount */}
      <Modal
        isOpen={billingOpen}
        toggle={() => { if (!billingSubmitting) { setBillingOpen(false); onFinished(); } }}
        centered
      >
        <ModalHeader toggle={() => { if (!billingSubmitting) { setBillingOpen(false); onFinished(); } }}>
          Submit billing
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            This will finalise the billed amount, generate the invoice, and close all work assignments.
            Status will move to <strong>Completed</strong>.
          </p>
          {workOrder ? (
            <p className="text-muted small mb-3">
              Current agreed amount:{" "}
              <span className="text-body fw-medium">
                {workOrder.agreedAmount != null
                  ? formatCurrency(workOrder.agreedAmount)
                  : "—"}
              </span>
            </p>
          ) : null}
          <Label className="form-label">
            Final billed amount <span className="text-muted fw-normal">(optional — leave blank to use agreed amount)</span>
          </Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="e.g. 15000"
            value={billingAmount}
            onChange={(e) => setBillingAmount(e.target.value)}
            disabled={billingSubmitting}
          />
          {draftAdvanceCount > 0 ? (
            <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
              Cannot proceed: {draftAdvanceCount} advance
              {draftAdvanceCount !== 1 ? "s" : ""} linked to this record{" "}
              {draftAdvanceCount !== 1 ? "are" : "is"} still awaiting receipt.
              All advances must be received or cancelled first.
            </div>
          ) : null}
          {workOrder ? (
            <LinkedClientAdvancesTable
              className="mt-3"
              sourceType={ClientAdvanceSourceType.WORK_ORDER}
              sourceId={workOrder.id}
              onLoaded={(advances: ClientAdvance[]) =>
                setDraftAdvanceCount(
                  advances.filter((a) => a.status === ClientAdvanceStatus.DRAFT)
                    .length,
                )
              }
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={billingSubmitting} onClick={() => { setBillingOpen(false); onFinished(); }}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={billingSubmitting || draftAdvanceCount > 0}
            onClick={() => void handleSubmitBilling()}
          >
            {billingSubmitting ? "Submitting…" : "Submit billing"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
